/**
 * Keeps the native Android geofence set in sync with the user's active,
 * place-tagged tasks and action items.
 *
 * The reconcile step is intentionally simple:
 *   1. Pull tasks and action items.
 *   2. Keep those with a non-null `place_id` and a non-terminal status.
 *   3. Deduplicate by place_id — one geofence per place.
 *   4. Hydrate place coordinates from /api/places.
 *   5. Cap at MAX_GEOFENCES (Android hard-limit is 100 per app).
 *   6. Call the native plugin's `replaceGeofences`.
 *
 * On web or if the native plugin is unavailable, this is a no-op that still
 * completes successfully so callers don't have to branch.
 */

import {
  HortonGeofence,
  isGeofenceSupported,
  fenceIdForPlace,
  coercePlaceId,
} from './geofencePlugin'
import type { GeofenceSpec } from './geofencePlugin'
import { placeService, type Place } from './placeService'
import { TaskService } from './taskService'
import type { Task } from './taskService'
import { ActionItemService } from './actionItemService'
import type { ActionItem } from './actionItemService'

const MAX_GEOFENCES = 80
// Inclusive by default: a task or action item is a geofence candidate unless
// its status is explicitly terminal. Prevents silent omission when the status
// vocabulary drifts (e.g., legacy "active" rows, or future states like
// "blocked"/"snoozed" that the filter hasn't been taught about yet).
const TERMINAL_TASK_STATUSES = new Set<string>([
  'completed', 'done', 'cancelled', 'canceled', 'archived', 'deleted',
])

function isTerminal(status: string | undefined | null): boolean {
  return typeof status === 'string' && TERMINAL_TASK_STATUSES.has(status)
}

const _taskService = new TaskService()
const _actionItemService = new ActionItemService()

interface PlaceGroup {
  placeId: number
  /** Representative title for the notification body. */
  primaryTitle: string
  /** Earliest due date (epoch ms) across all members; used when we must cap. */
  priority: number
}

function toTs(d: string | undefined | null): number {
  if (!d) return Number.POSITIVE_INFINITY
  const t = Date.parse(d)
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

function taskPlaceId(t: Task): number | null {
  return coercePlaceId(t.place_id)
}

function actionItemPlaceId(a: ActionItem): number | null {
  return coercePlaceId(a.place_id)
}

async function collectGroups(): Promise<PlaceGroup[]> {
  const [tasks, actionItems] = await Promise.all([
    _taskService.getTasks().catch(() => [] as Task[]),
    _actionItemService.getActionItems().catch(() => [] as ActionItem[]),
  ])

  const groups = new Map<number, PlaceGroup>()

  for (const t of tasks) {
    const pid = taskPlaceId(t)
    if (pid === null) continue
    if (isTerminal(t.status)) continue
    const existing = groups.get(pid)
    const candidatePriority = toTs(t.due_date)
    if (!existing) {
      groups.set(pid, {
        placeId: pid,
        primaryTitle: t.title,
        priority: candidatePriority,
      })
    } else if (candidatePriority < existing.priority) {
      existing.priority = candidatePriority
      existing.primaryTitle = t.title
    }
  }

  for (const a of actionItems) {
    const pid = actionItemPlaceId(a)
    if (pid === null) continue
    if (isTerminal(a.status)) continue
    const existing = groups.get(pid)
    const candidatePriority = toTs(a.due_date)
    if (!existing) {
      groups.set(pid, {
        placeId: pid,
        primaryTitle: a.title,
        priority: candidatePriority,
      })
    } else if (candidatePriority < existing.priority) {
      existing.priority = candidatePriority
      existing.primaryTitle = a.title
    }
  }

  const list = Array.from(groups.values())
  list.sort((a, b) => a.priority - b.priority || a.placeId - b.placeId)
  return list
}

export interface ReconcileResult {
  registered: number
  skipped: number
  supported: boolean
}

class GeofenceSyncService {
  private inflight: Promise<ReconcileResult> | null = null

  async reconcile(): Promise<ReconcileResult> {
    if (this.inflight) return this.inflight
    this.inflight = this._reconcileOnce()
    try {
      return await this.inflight
    } finally {
      this.inflight = null
    }
  }

  private async _reconcileOnce(): Promise<ReconcileResult> {
    if (!isGeofenceSupported()) {
      return { registered: 0, skipped: 0, supported: false }
    }

    let places: Place[] = []
    let groups: PlaceGroup[] = []
    try {
      ;[places, groups] = await Promise.all([
        placeService.list(),
        collectGroups(),
      ])
    } catch (err) {
      console.warn('[geofence-sync] failed to load tasks/places', err)
      return { registered: 0, skipped: 0, supported: true }
    }

    const placeMap = new Map<number, Place>()
    for (const p of places) placeMap.set(p.id, p)

    const fences: GeofenceSpec[] = []
    let skipped = 0
    for (const g of groups) {
      const p = placeMap.get(g.placeId)
      if (!p || !Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) {
        skipped += 1
        continue
      }
      fences.push({
        id: fenceIdForPlace(p.id),
        latitude: p.latitude,
        longitude: p.longitude,
        radius_m: p.radius_m || 150,
        dwell_ms: 60_000,
      })
      if (fences.length >= MAX_GEOFENCES) break
    }

    try {
      if (fences.length === 0) {
        await HortonGeofence.clearAll()
        return { registered: 0, skipped, supported: true }
      }
      const result = await HortonGeofence.replaceGeofences({ fences })
      return {
        registered: result?.registered ?? fences.length,
        skipped,
        supported: true,
      }
    } catch (err) {
      console.warn('[geofence-sync] native replaceGeofences failed', err)
      return { registered: 0, skipped, supported: true }
    }
  }

  async clearAll(): Promise<void> {
    if (!isGeofenceSupported()) return
    try {
      await HortonGeofence.clearAll()
    } catch (err) {
      console.warn('[geofence-sync] clearAll failed', err)
    }
  }
}

export const geofenceSyncService = new GeofenceSyncService()
export default geofenceSyncService
